import { useRef, useCallback } from 'react';

/**
 * useFormNavigation - Manages sequential field navigation in forms
 *
 * Usage:
 * const { registerField, focusNext, focusField } = useFormNavigation();
 *
 * <input ref={(el) => registerField('email', el)} enterKeyHint="next" />
 * <input ref={(el) => registerField('password', el)} enterKeyHint="done" />
 */
export function useFormNavigation(options = {}) {
  const { onSubmit } = options;
  const fieldsRef = useRef(new Map());
  const fieldOrderRef = useRef([]);

  // Register a field with its name and element reference
  const registerField = useCallback((name, element) => {
    if (element) {
      fieldsRef.current.set(name, element);
      // Maintain insertion order
      if (!fieldOrderRef.current.includes(name)) {
        fieldOrderRef.current.push(name);
      }
    } else {
      fieldsRef.current.delete(name);
      fieldOrderRef.current = fieldOrderRef.current.filter(n => n !== name);
    }
  }, []);

  // Focus a specific field by name
  const focusField = useCallback((name) => {
    const field = fieldsRef.current.get(name);
    if (field && typeof field.focus === 'function') {
      field.focus();
      return true;
    }
    return false;
  }, []);

  // Focus the next field in sequence
  const focusNext = useCallback((currentName) => {
    const order = fieldOrderRef.current;
    const currentIndex = order.indexOf(currentName);

    if (currentIndex === -1) return false;

    // Find next focusable field
    for (let i = currentIndex + 1; i < order.length; i++) {
      const nextField = fieldsRef.current.get(order[i]);
      if (nextField && !nextField.disabled && !nextField.readOnly) {
        nextField.focus();
        return true;
      }
    }

    // No more fields - trigger submit if provided
    if (onSubmit) {
      onSubmit();
    }

    return false;
  }, [onSubmit]);

  // Focus the previous field
  const focusPrevious = useCallback((currentName) => {
    const order = fieldOrderRef.current;
    const currentIndex = order.indexOf(currentName);

    if (currentIndex <= 0) return false;

    for (let i = currentIndex - 1; i >= 0; i--) {
      const prevField = fieldsRef.current.get(order[i]);
      if (prevField && !prevField.disabled && !prevField.readOnly) {
        prevField.focus();
        return true;
      }
    }

    return false;
  }, []);

  // Get field position info
  const getFieldInfo = useCallback((name) => {
    const order = fieldOrderRef.current;
    const index = order.indexOf(name);
    return {
      index,
      isFirst: index === 0,
      isLast: index === order.length - 1,
      total: order.length
    };
  }, []);

  // Clear all registered fields (useful for modal forms)
  const clearFields = useCallback(() => {
    fieldsRef.current.clear();
    fieldOrderRef.current = [];
  }, []);

  return {
    registerField,
    focusField,
    focusNext,
    focusPrevious,
    getFieldInfo,
    clearFields
  };
}

export default useFormNavigation;
